import React from 'react';
import { ExternalLink } from 'lucide-react';
import type { LscVodCandidate } from '../types';
import { TWITCH_VOD_EXPIRATION_COPY } from '../archiveCopy';
import { parseTwitchVodId } from '../twitchEmbed';
import { isVodCandidateUnavailable } from '../vodAvailability';
import TwitchPlayer from './TwitchPlayer';

export function getCandidateWatchVodVideoId(candidate: LscVodCandidate): string | null {
  return (
    candidate.provider_vod_id?.trim() ||
    parseTwitchVodId(candidate.url) ||
    null
  );
}

function formatVodDate(value?: string | null): string | null {
  if (!value) {
    return null;
  }
  try {
    return new Date(value).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}

interface CandidateWatchVodSectionProps {
  candidate: LscVodCandidate;
}

const CandidateWatchVodSection: React.FC<CandidateWatchVodSectionProps> = ({ candidate }) => {
  const videoId = getCandidateWatchVodVideoId(candidate);
  const unavailable = isVodCandidateUnavailable(candidate);
  const streamer = candidate.provider_broadcaster_login?.trim();
  const vodDate = formatVodDate(candidate.published_at ?? candidate.created_at);

  return (
    <section
      className="card lsc-candidate-watch space-y-4"
      data-testid="candidate-watch-vod-section"
      id="watch-vod"
    >
      <h2 className="text-lg font-semibold text-white">Watch VOD</h2>

      {unavailable ? (
        <div
          className="rounded-lg border border-secondary-700 bg-secondary-900/60 p-5 space-y-3"
          data-testid="candidate-watch-vod-unavailable"
        >
          <p className="font-medium text-white">This Twitch VOD is no longer available on Twitch.</p>
          <p className="text-sm text-secondary-400">
            LSC keeps the record for history, but it is hidden from review by default.
          </p>
          <dl className="grid gap-2 text-sm text-secondary-300">
            {candidate.title ? (
              <div>
                <dt className="text-secondary-500">Title</dt>
                <dd>{candidate.title}</dd>
              </div>
            ) : null}
            {streamer ? (
              <div>
                <dt className="text-secondary-500">Streamer</dt>
                <dd>@{streamer}</dd>
              </div>
            ) : null}
            {vodDate ? (
              <div>
                <dt className="text-secondary-500">Stream date</dt>
                <dd>{vodDate}</dd>
              </div>
            ) : null}
          </dl>
          {candidate.url ? (
            <a
              href={candidate.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-primary-300 hover:text-primary-200"
              data-testid="candidate-watch-vod-external-link"
            >
              <ExternalLink className="h-4 w-4" aria-hidden />
              Open on Twitch
            </a>
          ) : null}
        </div>
      ) : videoId ? (
        <TwitchPlayer
          mode="vod"
          videoId={videoId}
          title={candidate.title ?? 'Twitch VOD'}
          autoplay={false}
          muted={false}
          fallbackUrl={candidate.url}
          thumbnailUrl={candidate.thumbnail_url}
        />
      ) : (
        <div
          className="rounded-lg border border-secondary-700 bg-secondary-800/50 p-4"
          data-testid="candidate-watch-vod-missing-link"
        >
          <p className="font-medium text-white">Unavailable on Twitch</p>
          <p className="mt-1 text-sm text-secondary-400">
            This candidate does not include a playable Twitch VOD link yet.
          </p>
          {candidate.url && (
            <a
              href={candidate.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-sm text-primary-300 hover:text-primary-200"
              data-testid="candidate-watch-vod-external-link"
            >
              <ExternalLink className="h-4 w-4" aria-hidden />
              Open on Twitch
            </a>
          )}
        </div>
      )}

      <p className="text-xs text-secondary-500" data-testid="twitch-vod-expiration-copy">
        {TWITCH_VOD_EXPIRATION_COPY}
      </p>
    </section>
  );
};

export default CandidateWatchVodSection;
